import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { Chip, Icon, TableLoader, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { SimpleConfirmationModal } from 'src/components/shared/SimpleConfirmationModal';
import { UtilityAccountForm } from './UtilityAccountForm';
import useStyles from './styles';

/**
 * The utility account numbers that route inbound bills to this property (LB-5778).
 *
 * Bills arriving by email, scraping or bulk upload do not say which property they belong to. Until
 * now we worked that out from the service address printed on the bill, which is OCR'd text and often
 * does not match — so the bill waited in the review queue for someone to sort out by hand. A utility
 * account number is an identifier the provider assigns and prints identically every cycle, so adding
 * one here lets that property's bills attach on their own.
 *
 * A property with none configured keeps working exactly as before, on address matching.
 */
export const PropertyUtilityAccountsPage = observer(_ref => {
  var _state$pendingDelete;
  let mutablePropertyId = _ref.mutablePropertyId;
  const store = useStore();
  const classes = useStyles();
  const _useState = useState({
      accounts: [],
      isLoading: true,
      // Tracked separately from an empty list, because they mean opposite things: "none configured"
      // invites adding one, "we could not load them" must not.
      loadFailed: false,
      isSaving: false,
      editing: null,
      // null = not editing, 'new' = adding, otherwise the row being edited
      pendingDelete: null
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];

  // The property whose responses we are still willing to accept: switching property mid-load must
  // not repopulate this list with the previous one's numbers.
  const activePropertyId = useRef(null);
  const loadAccounts = async propertyId => {
    activePropertyId.current = propertyId;
    // Anything staged before the load is against a list that is about to be replaced. The one that
    // bites: switching property while the delete confirmation is open would leave pendingDelete
    // holding the OLD property's row, and confirming would fire a delete for that row id under the
    // NEW property. Clearing it here closes the modal instead.
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      isLoading: true,
      editing: null,
      pendingDelete: null
    }));
    const resp = await store.propertyUtility.getPropertyUtilityAccountsAsync.run({
      propertyId
    });
    if (activePropertyId.current !== propertyId) return;

    // run() resolves undefined on failure, so an unchecked `resp?.response || []` renders the empty
    // state on a failed load -- telling the PM there are no numbers configured when we simply do not
    // know. They would then add one that already exists.
    const failed = !resp || resp.isSuccessful === false;
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      accounts: failed ? [] : resp.response || [],
      loadFailed: failed,
      isLoading: false
    }));
  };
  useEffect(() => {
    if (!mutablePropertyId) return undefined;
    loadAccounts(mutablePropertyId);
    return () => {
      activePropertyId.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutablePropertyId]);
  const handleSave = async values => {
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      isSaving: true
    }));
    const editingRow = state.editing !== 'new' ? state.editing : null;
    const action = editingRow ? store.propertyUtility.updatePropertyUtilityAccountAsync.run(_objectSpread({
      propertyId: mutablePropertyId,
      accountId: editingRow.id
    }, values)) : store.propertyUtility.addPropertyUtilityAccountAsync.run(_objectSpread({
      propertyId: mutablePropertyId
    }, values));
    const resp = await action;
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      isSaving: false
    }));

    // Repo convention: the async layer raises the error toast itself, so returning quietly here
    // avoids a second one. The server's message (duplicate number, for instance) is the useful one.
    if (!resp || resp.isSuccessful === false) return;
    toast('success', {
      body: [editingRow ? 'Account number updated' : 'Account number added']
    });
    await loadAccounts(mutablePropertyId);
  };
  const handleDeleteConfirm = async _ref2 => {
    let action = _ref2.action;
    const row = state.pendingDelete;
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      pendingDelete: null
    }));
    if (action !== 'submit' || !row) return;
    const resp = await store.propertyUtility.deletePropertyUtilityAccountAsync.run({
      propertyId: mutablePropertyId,
      accountId: row.id
    });
    if (!resp || resp.isSuccessful === false) return;
    toast('success', {
      body: ['Account number removed']
    });
    await loadAccounts(mutablePropertyId);
  };
  const renderRow = account => /*#__PURE__*/React.createElement("div", {
    className: classes.row,
    key: account.id
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cell,
    style: {
      flex: 2
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, account.accountNumber)), /*#__PURE__*/React.createElement("div", {
    className: classes.cell,
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light"
  }, account.serviceType || 'All services')), /*#__PURE__*/React.createElement("div", {
    className: classes.cell,
    style: {
      flex: 1
    }
  }, account.isDefaultForAccountNumber && /*#__PURE__*/React.createElement(Chip, {
    label: "Default",
    variant: "info"
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.cell,
    style: {
      flex: 2
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light"
  }, account.addedBy || '—')), /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    "aria-label": "Edit account number",
    onClick: () => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      editing: account
    }))
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "edit"
  })), /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    "aria-label": "Remove account number",
    onClick: () => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      pendingDelete: account
    }))
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.section
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.intro
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    style: {
      fontWeight: 600
    }
  }, "Bill routing"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    color: "light"
  }, "Add the utility account numbers that appear on this property's bills. Bills quoting a number listed here attach to this property automatically, instead of waiting for someone to match them by address.")), state.isLoading ? /*#__PURE__*/React.createElement(TableLoader, null) : /*#__PURE__*/React.createElement(React.Fragment, null, state.accounts.length > 0 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.row
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.headerCell,
    style: {
      flex: 2
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Account number")), /*#__PURE__*/React.createElement("div", {
    className: classes.headerCell,
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Service")), /*#__PURE__*/React.createElement("div", {
    className: classes.headerCell,
    style: {
      flex: 1
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.headerCell,
    style: {
      flex: 2
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Added by")), /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  })), state.accounts.map(renderRow)), state.loadFailed && /*#__PURE__*/React.createElement("div", {
    className: classes.empty
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "We couldn't load this property's account numbers."), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => loadAccounts(mutablePropertyId)
  }, "Try again")), !state.loadFailed && state.accounts.length === 0 && state.editing === null && /*#__PURE__*/React.createElement("div", {
    className: classes.empty
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light"
  }, "No account numbers yet. This property's bills are matched by service address, which is less reliable.")), state.editing !== null ? /*#__PURE__*/React.createElement(UtilityAccountForm
  // Remount per row, so the form seeds from the row being edited rather than keeping
  // whatever the previous edit left behind.
  , {
    key: state.editing === 'new' ? 'new' : state.editing.id,
    initialValue: state.editing === 'new' ? null : state.editing,
    isSaving: state.isSaving,
    onSubmit: handleSave,
    onCancel: () => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      editing: null
    }))
  }) : /*#__PURE__*/React.createElement("div", {
    hidden: state.loadFailed
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      editing: 'new'
    }))
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "plus"
  }), " Add account number")))), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: "Remove account number",
    subTitleText: (_state$pendingDelete = state.pendingDelete) === null || _state$pendingDelete === void 0 ? void 0 : _state$pendingDelete.accountNumber,
    bodyText: 'Bills quoting this number will no longer attach to this property automatically. ' + 'They will fall back to matching on the service address.',
    externalIsOpen: !!state.pendingDelete,
    objectToPassOver: state.pendingDelete,
    callBack: handleDeleteConfirm,
    customTrigger: null
  }));
});